"use client";

import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cardVariants } from "@/lib/animations";
import LazyImage from "@/components/ui/LazyImage";
import { transformMediaUrl } from "@/lib/media-url-transformer";

export interface Reference {
    year: number | null;
    subject: string;
    description: string | null;
    media: {
        id: number;
        url: string;
        type: 'image' | 'video';
        thumbnail?: string;
    } | null;
}

interface ProductMiceReferencesProps {
    references: Reference[];
    language?: string;
}

export default function ProductMiceReferences({
    references,
}: ProductMiceReferencesProps) {
    const [modalRef, setModalRef] = useState<Reference | null>(null);

    const openModal = useCallback((ref: Reference) => {
        setModalRef(ref);
    }, []);

    const closeModal = useCallback(() => {
        setModalRef(null);
    }, []);

    if (!references || references.length === 0) {
        return null;
    }

    const sortedReferences = [...references].sort((a, b) => {
        if (a.year && b.year) return b.year - a.year;
        if (a.year) return -1;
        if (b.year) return 1;
        return 0;
    });

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedReferences.map((reference, index) => (
                    <motion.button
                        type="button"
                        key={`${reference.year}-${reference.subject}-${index}`}
                        variants={cardVariants}
                        onClick={() => openModal(reference)}
                        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow text-left w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2"
                        aria-label={reference.subject}
                    >
                        {reference.media && (
                            <div className="relative w-full aspect-video bg-gray-100">
                                {reference.media.type === 'video' ? (
                                    <video
                                        className="w-full h-full object-cover"
                                        preload="metadata"
                                        muted
                                        playsInline
                                        aria-hidden
                                    >
                                        <source src={transformMediaUrl(reference.media.url)} type="video/mp4" />
                                    </video>
                                ) : (
                                    <LazyImage
                                        src={transformMediaUrl(reference.media.url)}
                                        alt={reference.subject}
                                        fill
                                        className="object-cover"
                                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                    />
                                )}
                            </div>
                        )}
                        <div className="p-6">
                            {reference.year && (
                                <span className="inline-block text-sm font-semibold text-alsero-first bg-alsero-first/10 px-3 py-1 rounded-full mb-2">
                                    {reference.year}
                                </span>
                            )}
                            <h3 className="text-xl font-bold text-alsero-dark-gray mb-3">
                                {reference.subject}
                            </h3>
                            {reference.description && (
                                <p className="text-alsero-text-tour leading-relaxed line-clamp-3">
                                    {reference.description}
                                </p>
                            )}
                        </div>
                    </motion.button>
                ))}
            </div>

            <AnimatePresence>
                {modalRef && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
                        onClick={closeModal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="reference-modal-title"
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                        >
                            <div className="p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <h2 id="reference-modal-title" className="text-2xl font-bold text-alsero-dark-gray pr-4">
                                        {modalRef.subject}
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="p-2 rounded-full hover:bg-gray-100 text-gray-600 focus:outline-none focus:ring-2 focus:ring-alsero-first"
                                        aria-label="Close"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                                {modalRef.year && (
                                    <span className="inline-block text-sm font-semibold text-alsero-first bg-alsero-first/10 px-3 py-1 rounded-full mb-4">
                                        {modalRef.year}
                                    </span>
                                )}
                                {modalRef.media && (
                                    <div className="relative w-full aspect-video bg-gray-100 rounded-lg overflow-hidden mb-4">
                                        {modalRef.media.type === 'video' ? (
                                            <video
                                                controls
                                                poster={modalRef.media.thumbnail ? transformMediaUrl(modalRef.media.thumbnail) : undefined}
                                                className="w-full h-full object-cover"
                                                preload="metadata"
                                            >
                                                <source src={transformMediaUrl(modalRef.media.url)} type="video/mp4" />
                                            </video>
                                        ) : (
                                            <LazyImage
                                                src={transformMediaUrl(modalRef.media.url)}
                                                alt={modalRef.subject}
                                                fill
                                                className="object-cover"
                                                sizes="(max-width: 768px) 100vw, 672px"
                                            />
                                        )}
                                    </div>
                                )}
                                {modalRef.description && (
                                    <div className="text-alsero-text-tour leading-relaxed prose prose-lg max-w-none">
                                        <p>{modalRef.description}</p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

